import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import {
  TextField,
  InputAdornment,
  IconButton,
  FormHelperText,
  InputLabel,
  FormControl,
  OutlinedInput,
} from '@mui/material';
import {
  Lock,
  Visibility,
  VisibilityOff,
  CheckCircleOutline,
  ErrorOutline,
  LockReset,
} from '@mui/icons-material';

import Button from '../components/formElements/Button';
import { useAuth } from '../context/AuthContext';

type Props = {
  setSubmitted: (submitted: boolean) => void;
  token: string;
};

type FormValues = {
  password: string;
  confirmPassword: string;
};

function ResetPasswordForm(props: Props) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors, isValid, isDirty },
  } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const password = watch('password', '');
  const confirmPassword = watch('confirmPassword', '');

  const { resetPassword } = useAuth();

  const onSubmit = async (data: FormValues) => {
    setIsLoading(true);
    const toastId = toast.loading('Resetowanie hasła...', {
      position: 'top-right',
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: 0,
      theme: 'colored',
    });

    try {
      await resetPassword(data.password, props.token);
      toast.update(toastId, {
        render: 'Hasło zostało pomyślnie zresetowane!',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });
      props.setSubmitted(true);
    } catch (error: Error | any) {
      console.error('Reset password failed', error);
      toast.update(toastId, {
        render: error.message || 'Wystąpił błąd podczas resetowania hasła',
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Warunki sprawdzania siły hasła
  const hasMinLength = password.length >= 8;
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecialChar = /[^A-Za-z0-9]/.test(password);
  const passwordsMatch =
    password === confirmPassword && confirmPassword.length > 0;

  // Określenie siły hasła
  const getPasswordStrength = () => {
    if (password.length === 0) return 0;
    let strength = 0;
    if (hasMinLength) strength++;
    if (hasUpperCase) strength++;
    if (hasLowerCase) strength++;
    if (hasNumber) strength++;
    if (hasSpecialChar) strength++;
    return (strength / 5) * 100;
  };

  const passwordStrength = getPasswordStrength();

  // Kolor paska siły hasła
  const getStrengthColor = () => {
    if (passwordStrength < 40) return 'bg-red-500';
    if (passwordStrength < 70) return 'bg-amber-500';
    return 'bg-green-500';
  };

  // Animacje
  const formVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 24,
      },
    },
  };

  return (
    <motion.form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col space-y-5 items-stretch w-full"
      variants={formVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div variants={itemVariants} className="space-y-4">
        <FormControl variant="outlined" fullWidth>
          <InputLabel
            htmlFor="password"
            error={!!errors.password}
            className={!errors.password && password ? 'text-primary' : ''}
          >
            Nowe hasło
          </InputLabel>
          <Controller
            name="password"
            control={control}
            rules={{
              required: 'Hasło jest wymagane',
              pattern: {
                value: /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[a-zA-Z]).{8,}$/,
                message: 'Hasło musi spełniać wymagania bezpieczeństwa',
              },
            }}
            render={({ field }) => (
              <OutlinedInput
                {...field}
                id="password"
                type={showPassword ? 'text' : 'password'}
                error={!!errors.password}
                className="bg-white rounded-lg"
                startAdornment={
                  <InputAdornment position="start">
                    <Lock
                      color={
                        errors.password
                          ? 'error'
                          : password
                            ? 'primary'
                            : 'action'
                      }
                    />
                  </InputAdornment>
                }
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderRadius: '0.5rem',
                  },
                }}
              />
            )}
          />
          {errors.password && (
            <FormHelperText error>{errors.password.message}</FormHelperText>
          )}
        </FormControl>

        {/* Pasek siły hasła */}
        {password && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            transition={{ duration: 0.3 }}
            className="space-y-2"
          >
            <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
              <motion.div
                className={`h-full ${getStrengthColor()}`}
                initial={{ width: '0%' }}
                animate={{ width: `${passwordStrength}%` }}
                transition={{ duration: 0.5 }}
              ></motion.div>
            </div>

            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="flex items-center">
                {hasMinLength ? (
                  <CheckCircleOutline
                    fontSize="small"
                    className="mr-1 text-green-500"
                  />
                ) : (
                  <ErrorOutline
                    fontSize="small"
                    className="mr-1 text-gray-400"
                  />
                )}
                <span
                  className={hasMinLength ? 'text-green-700' : 'text-gray-500'}
                >
                  Minimum 8 znaków
                </span>
              </div>
              <div className="flex items-center">
                {hasUpperCase ? (
                  <CheckCircleOutline
                    fontSize="small"
                    className="mr-1 text-green-500"
                  />
                ) : (
                  <ErrorOutline
                    fontSize="small"
                    className="mr-1 text-gray-400"
                  />
                )}
                <span
                  className={hasUpperCase ? 'text-green-700' : 'text-gray-500'}
                >
                  Wielka litera
                </span>
              </div>
              <div className="flex items-center">
                {hasLowerCase ? (
                  <CheckCircleOutline
                    fontSize="small"
                    className="mr-1 text-green-500"
                  />
                ) : (
                  <ErrorOutline
                    fontSize="small"
                    className="mr-1 text-gray-400"
                  />
                )}
                <span
                  className={hasLowerCase ? 'text-green-700' : 'text-gray-500'}
                >
                  Mała litera
                </span>
              </div>
              <div className="flex items-center">
                {hasNumber ? (
                  <CheckCircleOutline
                    fontSize="small"
                    className="mr-1 text-green-500"
                  />
                ) : (
                  <ErrorOutline
                    fontSize="small"
                    className="mr-1 text-gray-400"
                  />
                )}
                <span
                  className={hasNumber ? 'text-green-700' : 'text-gray-500'}
                >
                  Cyfra
                </span>
              </div>
              <div className="flex items-center">
                {hasSpecialChar ? (
                  <CheckCircleOutline
                    fontSize="small"
                    className="mr-1 text-green-500"
                  />
                ) : (
                  <ErrorOutline
                    fontSize="small"
                    className="mr-1 text-gray-400"
                  />
                )}
                <span
                  className={
                    hasSpecialChar ? 'text-green-700' : 'text-gray-500'
                  }
                >
                  Znak specjalny
                </span>
              </div>
            </div>
          </motion.div>
        )}

        <FormControl variant="outlined" fullWidth>
          <InputLabel
            htmlFor="confirmPassword"
            error={!!errors.confirmPassword}
            className={
              !errors.confirmPassword && confirmPassword && passwordsMatch
                ? 'text-primary'
                : ''
            }
          >
            Powtórz hasło
          </InputLabel>
          <Controller
            name="confirmPassword"
            control={control}
            rules={{
              required: 'Powtórzenie hasła jest wymagane',
              validate: (value) =>
                value === password || 'Hasła muszą się zgadzać',
            }}
            render={({ field }) => (
              <OutlinedInput
                {...field}
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                error={!!errors.confirmPassword}
                className="bg-white rounded-lg"
                startAdornment={
                  <InputAdornment position="start">
                    <Lock
                      color={
                        errors.confirmPassword
                          ? 'error'
                          : confirmPassword && passwordsMatch
                            ? 'primary'
                            : 'action'
                      }
                    />
                  </InputAdornment>
                }
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderRadius: '0.5rem',
                  },
                }}
              />
            )}
          />
          {errors.confirmPassword ? (
            <FormHelperText error>
              {errors.confirmPassword.message}
            </FormHelperText>
          ) : confirmPassword && passwordsMatch ? (
            <FormHelperText className="text-green-600 flex items-center">
              <CheckCircleOutline fontSize="small" className="mr-1" />
              Hasła są identyczne
            </FormHelperText>
          ) : null}
        </FormControl>
      </motion.div>

      <motion.div
        variants={itemVariants}
        whileHover={{ scale: isDirty && isValid ? 1.02 : 1 }}
        whileTap={{ scale: isDirty && isValid ? 0.98 : 1 }}
        className="pt-2"
      >
        <Button
          type="submit"
          colorTheme="primary"
          disabled={!isDirty || !isValid || isLoading}
          className="w-full h-12 !rounded-lg transition-all duration-300 relative overflow-hidden"
        >
          <motion.div
            className="flex items-center justify-center gap-2 w-full"
            initial={false}
            animate={isLoading ? { x: -10 } : { x: 0 }}
          >
            <LockReset />
            <span>Ustaw nowe hasło</span>
          </motion.div>

          {isLoading && (
            <motion.div
              className="absolute inset-0 flex items-center justify-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            </motion.div>
          )}
        </Button>
      </motion.div>
    </motion.form>
  );
}

export default ResetPasswordForm;
